import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "utilities/auth";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { Search, Building2, X, Check } from "lucide-react";

/**
 * Company picker modal for "Create Application" entrypoint on the MID
 * applications list page. Internal-only; the caller guards by role.
 *
 * Filters:
 *   - companyType === "client"
 *   - primary application status === "approved"
 *   - excludes `sound-admin`
 *
 * Approach: query `GET /applications?status=approved&search=<term>` — this
 * returns one doc per company that already has an approved registration, with
 * `companyId` and `businessInfo.legalName` populated. No new backend endpoint
 * needed; client-side filtering only removes sound-admin and anything with a
 * missing companyId. We search the applications endpoint so the returned
 * list is already filtered to approved — this avoids the "company has an app
 * but not approved yet" false positives that a companies-first approach would
 * hit.
 */
export default function SelectCompanyModal({ onClose, onSelect }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCandidates(query);
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const fetchCandidates = async (search) => {
    if (!effectiveCompanyId || !auth?.user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", "approved");
      params.set("limit", "25");
      params.set("sort", "-updatedAt");
      if (search) params.set("search", search);

      const res = await makeApiRequest(
        "get",
        `${apiRoutes.getAllApplications}?${params.toString()}`,
        auth.user,
        effectiveCompanyId
      );

      const apps = Array.isArray(res?.data?.data) ? res.data.data : [];

      // Shape each row for the picker. Prefer legalName, fall back to the
      // first DBA name, then the companyId as a last resort.
      const normalized = apps
        .filter((app) => app && app.companyId)
        .filter(
          (app) =>
            app.companyId !== "sound-admin" && app.slug !== "sound-admin"
        )
        .map((app) => ({
          companyId: app.companyId,
          name:
            app.businessInfo?.legalName ||
            app.dbas?.[0]?.dbaName ||
            app.companyId,
          subtitle:
            app.dbas?.[0]?.dbaName &&
            app.businessInfo?.legalName &&
            app.dbas[0].dbaName !== app.businessInfo.legalName
              ? app.dbas[0].dbaName
              : null,
          applicationId: app.applicationId || app._id || null,
        }));

      // De-duplicate by companyId just in case.
      const seen = new Set();
      const unique = [];
      for (const row of normalized) {
        if (seen.has(row.companyId)) continue;
        seen.add(row.companyId);
        unique.push(row);
      }
      setResults(unique);
    } catch (err) {
      console.error("[SelectCompanyModal] Search failed", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!selected) return;
    onSelect?.(selected);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-lg shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Create Application</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Pick an approved client to create a new MID application for.
            </p>
          </div>
          <button
            className="btn btn-ghost h-8 w-8 p-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            className="input input-bordered w-full pl-9"
            placeholder="Search approved clients by name or ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="max-h-80 overflow-y-auto -mx-2 px-2 mb-3">
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {query
                ? "No approved clients match that search."
                : "No approved clients found."}
            </div>
          ) : (
            <ul className="space-y-1">
              {results.map((c) => {
                const isSelected = selected?.companyId === c.companyId;
                return (
                  <li key={c.companyId}>
                    <button
                      type="button"
                      onClick={() => setSelected(c)}
                      className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg border transition-colors ${
                        isSelected
                          ? "border-brand bg-brand/10"
                          : "border-transparent hover:border-border hover:bg-muted/60"
                      }`}
                    >
                      <div
                        className={`p-1.5 rounded-md ${
                          isSelected ? "bg-brand/20" : "bg-muted"
                        }`}
                      >
                        <Building2
                          className={`h-4 w-4 ${
                            isSelected
                              ? "text-brand"
                              : "text-muted-foreground"
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {c.name}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.subtitle ? `${c.subtitle} · ` : ""}
                          {c.companyId}
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="h-4 w-4 text-brand shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t border-border">
          <button className="btn btn-ghost flex-1" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary flex-1"
            onClick={handleConfirm}
            disabled={!selected}
          >
            {selected ? `Continue with ${selected.name}` : "Select a client"}
          </button>
        </div>
      </div>
    </div>
  );
}
